// usePortalCollision.ts - Portal Collision Detection for Diegetic Navigation
// Detects when player enters portal torus and triggers game load

import { useRef, useCallback, useEffect, useState } from 'react'
import * as THREE from 'three'

interface PortalConfig {
  game: string
  color: string
  position: [number, number, number]
  active: boolean
}

interface CollisionState {
  isColliding: boolean
  isNearPortal: boolean
  collidedPortal: PortalConfig | null
  collisionDistance: number
  collisionProgress: number // 0-1 showing how close to threshold
}

interface UsePortalCollisionReturn {
  isColliding: boolean
  isNearPortal: boolean
  collidedPortal: PortalConfig | null
  collisionDistance: number
  collisionProgress: number
  checkCollision: (playerPosition: THREE.Vector3, activePortal: PortalConfig | null) => boolean
  resetCollision: () => void
  cancelTransition: () => void
}

// Portal collision constants
const PORTAL_RADIUS = 1.8       // Radius of portal torus (was 1.0)
const PORTAL_DEPTH = 0.5        // Depth threshold for "entering" portal
const COLLISION_THRESHOLD = 0.3 // Distance threshold for collision
const PROXIMITY_RADIUS = 3.0    // For visual feedback when near portal
const TRANSITION_DELAY = 500    // ms grace period before triggering onPortalEnter

export function usePortalCollision(
  onPortalEnter?: (game: string) => void
): UsePortalCollisionReturn {
  const [state, setState] = useState<CollisionState>({
    isColliding: false,
    isNearPortal: false,
    collidedPortal: null,
    collisionDistance: Infinity,
    collisionProgress: 0
  })

  const lastCollisionTime = useRef(0)
  const collisionCooldown = 2000 // 2 second cooldown between collisions
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingPortalRef = useRef<PortalConfig | null>(null)

  // Cancel any pending transition
  const cancelTransition = useCallback(() => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current)
      transitionTimeoutRef.current = null
      pendingPortalRef.current = null
    }
  }, [])

  // Check if player position intersects with portal
  const checkCollision = useCallback((
    playerPosition: THREE.Vector3,
    activePortal: PortalConfig | null
  ): boolean => {
    if (!activePortal || !activePortal.active) {
      if (state.isColliding || state.isNearPortal) {
        setState(prev => ({ 
          ...prev, 
          isColliding: false, 
          isNearPortal: false,
          collidedPortal: null,
          collisionProgress: 0
        }))
      }
      // Cancel any pending transition if portal becomes inactive
      if (transitionTimeoutRef.current) {
        cancelTransition()
      }
      return false
    }

    const portalPos = new THREE.Vector3(...activePortal.position)
    
    // Calculate distance to portal center
    const distanceToPortal = playerPosition.distanceTo(portalPos)
    
    // Check if within portal radius
    const horizontalDistance = Math.sqrt(
      Math.pow(playerPosition.x - portalPos.x, 2) +
      Math.pow(playerPosition.z - portalPos.z, 2)
    )
    
    // Calculate collision progress (0-1, where 1 is at the threshold)
    const progress = Math.max(0, Math.min(1, 1 - (horizontalDistance / PORTAL_RADIUS)))
    
    // Check proximity for visual feedback
    const isNear = distanceToPortal < PROXIMITY_RADIUS
    
    // Player must be:
    // 1. Within the portal ring radius
    // 2. Close enough to the portal plane (z-depth)
    // 3. Past cooldown period
    const now = Date.now()
    const isWithinRadius = horizontalDistance < PORTAL_RADIUS
    const isWithinDepth = Math.abs(playerPosition.z - portalPos.z) < PORTAL_DEPTH
    const isPastCooldown = now - lastCollisionTime.current > collisionCooldown
    
    const shouldCollide = isWithinRadius && isWithinDepth && isPastCooldown

    // Update near portal state
    if (isNear !== state.isNearPortal) {
      setState(prev => ({ ...prev, isNearPortal: isNear }))
    }

    // Update collision progress
    if (progress !== state.collisionProgress) {
      setState(prev => ({ ...prev, collisionProgress: progress }))
    }

    if (shouldCollide && !state.isColliding) {
      // Collision detected - start grace period
      lastCollisionTime.current = now
      pendingPortalRef.current = activePortal
      
      setState({
        isColliding: true,
        isNearPortal: true,
        collidedPortal: activePortal,
        collisionDistance: distanceToPortal,
        collisionProgress: 1
      })
      
      // Set up grace period before triggering game load
      transitionTimeoutRef.current = setTimeout(() => {
        if (pendingPortalRef.current) {
          // Trigger game load callback
          onPortalEnter?.(pendingPortalRef.current.game)
          console.log(`[PortalCollision] ENTERED PORTAL: ${pendingPortalRef.current.game}`)
        }
        transitionTimeoutRef.current = null
        pendingPortalRef.current = null
      }, TRANSITION_DELAY)
      
      return true
    }

    // Update distance even if not colliding
    if (!shouldCollide) {
      setState(prev => ({
        ...prev,
        collisionDistance: distanceToPortal,
        isColliding: false,
        collidedPortal: null
      }))
    }

    return shouldCollide
  }, [state.isColliding, state.isNearPortal, state.collisionProgress, onPortalEnter, cancelTransition])

  // Reset collision state (e.g., after game load)
  const resetCollision = useCallback(() => {
    // Cancel any pending transition
    cancelTransition()
    
    setState({
      isColliding: false,
      isNearPortal: false,
      collidedPortal: null,
      collisionDistance: Infinity,
      collisionProgress: 0
    })
    lastCollisionTime.current = 0
  }, [cancelTransition])

  // Debug visualization (optional - remove in production)
  useEffect(() => {
    // Only log in development
    if (import.meta.env.DEV) {
      console.log(`[PortalCollision] Distance: ${state.collisionDistance.toFixed(2)}m, Near: ${state.isNearPortal}, Progress: ${(state.collisionProgress * 100).toFixed(0)}%`)
    }
  }, [state.collisionDistance, state.isNearPortal, state.collisionProgress])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelTransition()
    }
  }, [cancelTransition])

  return {
    isColliding: state.isColliding,
    isNearPortal: state.isNearPortal,
    collidedPortal: state.collidedPortal,
    collisionDistance: state.collisionDistance,
    collisionProgress: state.collisionProgress,
    checkCollision,
    resetCollision,
    cancelTransition
  }
}

// Alternative: Raycast-based collision for more precise entry detection
export function usePortalRaycast(
  onPortalEnter?: (game: string) => void
): {
  castRay: (origin: THREE.Vector3, direction: THREE.Vector3, activePortal: PortalConfig | null) => boolean
  isColliding: boolean
} {
  const [isColliding, setIsColliding] = useState(false)
  const raycaster = useRef(new THREE.Raycaster())

  const castRay = useCallback((
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    activePortal: PortalConfig | null
  ): boolean => {
    if (!activePortal || !activePortal.active) {
      setIsColliding(false)
      return false
    }

    // Create a virtual portal plane for raycasting
    const portalPos = new THREE.Vector3(...activePortal.position)
    const portalPlane = new THREE.Plane(
      new THREE.Vector3(0, 0, 1), // Facing forward
      -portalPos.z
    )

    raycaster.current.set(origin, direction)
    
    // Check intersection with portal plane
    const target = new THREE.Vector3()
    const intersects = raycaster.current.ray.intersectPlane(portalPlane, target)

    if (intersects) {
      const distanceToPortal = target.distanceTo(portalPos)
      const isHit = distanceToPortal < PORTAL_RADIUS
      
      if (isHit && !isColliding) {
        setIsColliding(true)
        onPortalEnter?.(activePortal.game)
        return true
      }
    }

    setIsColliding(false)
    return false
  }, [isColliding, onPortalEnter])

  return { castRay, isColliding }
}

export default usePortalCollision
